import {
  UserData,
  UserRequest,
  useCompanies,
  useParentCompanies,
  useDealers,
  useUser,
  useUpdateUser,
  useRegister,
  useGetImage,
} from '@api'
import {FormFieldType, ImageType, NavigationPathKey, TranslationKey, UserRole} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {useUploadImage} from 'api/endpoints/image/mutations'
import {
  createRadioBtnOptions,
  FormFieldItem,
  generateDropdownOptions,
  generateReadableErrorMessages,
  isCompanyUser,
  isSuperAdmin,
  roles,
  RolesFormData,
  validateFields,
  countries,
  getUserData,
  useViewNavigate,
} from 'common'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useLocation, useParams} from 'react-router-dom'

import {UIKit} from '@components'

/**
 * Initial data
 */
const initialUserData: UserData = {
  fullName: '',
  password: '',
  email: '',
  active: true,
  role: UserRole.COMPANY_USER,
  dealerId: null,
  companyIds: [],
  image: '',
}
export function useUserFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [userData, setUserData] = useState<UserData>(initialUserData)
  const [userFormFieldsErrors, setUserFormFieldsErrors] = useState<string[]>([])
  const [selectedParentCompany, setSelectedParentCompany] = useState<string | undefined>(undefined)
  const [imageFile, setImageFile] = useState<undefined | File>(undefined)
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const dealerId = queryParams.get('dealerId')

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const {data: userResponse} = useUser(id)
  const {data: dealersData} = useDealers()
  const {data: companiesData} = useCompanies()
  const {data: parentCompaniesData} = useParentCompanies()
  const {data: image} = useGetImage(ImageType.USER, id)

  const {mutate: updateUserMutation} = useUpdateUser()
  const {mutate: registerUserMutation} = useRegister()
  const {mutate: postImageMutation} = useUploadImage()

  const companiesOptions = useMemo(() => generateDropdownOptions(companiesData || []), [companiesData])
  const parentCompaniesOptions = useMemo(
    () => generateDropdownOptions(parentCompaniesData || []),
    [parentCompaniesData],
  )

  const imageUrl = useMemo(() => userData.image, [userData])

  useEffect(() => {
    if (id && userResponse) {
      setUserData({
        active: userResponse.active,
        companyIds: userResponse.companyIds,
        dealerId: userResponse.dealerId,
        email: userResponse.email,
        fullName: userResponse.fullName,
        image: image || '',
        role: userResponse.role,
        password: '',
      })
      setSelectedParentCompany(companiesData?.find(item => userResponse.companyIds.includes(item.id))?.parentCompanyId)
    } else {
      setUserData(prev => ({...prev, companyIds: [userSelection?.companyId || '']}))
    }
    if (dealerId) setUserData(prev => ({...prev, dealerId}))
  }, [id, userResponse, companiesData, userSelection, dealerId])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey['Products and accessories']), link: NavigationPathKey.PRODUCT_DASHBOARD},
        {title: t(TranslationKey['User search']), link: NavigationPathKey.USER_SEARCH},
        {title: id ? t(TranslationKey['Edit user']) : t(TranslationKey['New user'])},
      ]),
    [id],
  )

  /**
   * Dealer options filtered by Company ID
   */
  const filteredDealers: UIKit.DropdownItem[] = useMemo(
    () =>
      generateDropdownOptions(
        (dealersData || [])?.filter(item => !userData.companyIds[0] || item.companyId === userData.companyIds[0]),
      ),
    [dealersData, userData.companyIds],
  )

  /**
   * A function that handles User form fields change
   * @param event Change event
   * @param checked Checked state for checkbox form fields
   */
  const onChangeUserGeneralFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked?: boolean,
  ) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = checked ?? event.target.value
    setUserFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setUserData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * A function that handles User dropdown form field change
   * @param fieldName Field name
   * @param item Dropdown item
   */
  const onChangeUserAdditionalFormField = (fieldName: string, item: UIKit.DropdownItem | null) => {
    setIsFormDirty(true)
    setUserFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    if (fieldName === 'companyIds' && userData.dealerId && item !== null) {
      const currentDealer = dealersData?.find(item => item.id === userData.dealerId)
      if (currentDealer?.companyId !== item?.value) setUserData(prev => ({...prev, dealerId: null}))
      setUserData(prev => ({...prev, companyIds: [String(item.value)]}))
    } else setUserData(prev => ({...prev, [fieldName]: item?.value ?? null}))
  }

  /**
   * A function that handles image file drop
   * @param acceptedFiles File
   */
  const handleDrop = (acceptedFiles: File[]): void => {
    setImageFile(acceptedFiles[0])
    setUserData(prev => ({...prev, image: URL.createObjectURL(acceptedFiles[0])}))
  }

  /**
   * User form fields configuration - general section
   */
  const userGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Full name']),
      name: 'fullName',
      onChange: onChangeUserGeneralFormField,
      value: userData.fullName,
      error: userFormFieldsErrors.includes('fullName'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Email),
      name: 'email',
      onChange: onChangeUserGeneralFormField,
      value: userData.email,
      error: userFormFieldsErrors.includes('email'),
    },
    ...(id
      ? [
          {
            fieldType: FormFieldType.CHECKBOX_GROUP,
            name: 'active',
            label: t(TranslationKey.Active),
            checkboxes: [
              {
                checked: userData.active,
                label: t(TranslationKey.Active),
                value: userData.active,
                name: 'active',
                onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                  onChangeUserGeneralFormField(event, checked),
              },
            ],
          },
          ...(isSuperAdmin()
            ? [
                {
                  fieldType: FormFieldType.INPUT,
                  label: t(TranslationKey['New password']),
                  type: 'password',
                  name: 'password',
                  onChange: onChangeUserGeneralFormField,
                  value: userData.password,
                  error: userFormFieldsErrors.includes('password'),
                },
              ]
            : []),
        ]
      : [
          {
            fieldType: FormFieldType.INPUT,
            label: t(TranslationKey.Password),
            type: 'password',
            name: 'password',
            onChange: onChangeUserGeneralFormField,
            value: userData.password,
            error: userFormFieldsErrors.includes('password'),
            disabled: !!id,
          },
        ]),
  ]

  /**
   * User form fields configuration - role section
   */
  const userRoleFormField: FormFieldItem = useMemo(
    () => ({
      fieldType: FormFieldType.RADIO_BUTTON_GROUP,
      name: 'role',
      label: t(TranslationKey.Role),
      itemsInColumn: 4,
      value: userData.role,
      radioButtons: createRadioBtnOptions(
        'code',
        'name',
        'role',
        // Super Administrator can assign all Roles
        // Company Administrator and Company User can only assign Company User Role
        (item: RolesFormData) => {
          if (isSuperAdmin() || item.code === UserRole.COMPANY_USER) return false
          else return true
        },
        (event: React.ChangeEvent<HTMLInputElement>) => onChangeUserGeneralFormField(event),
        roles(t),
      ),
      error: userFormFieldsErrors.includes('role'),
    }),
    [userData.role, userFormFieldsErrors],
  )

  const userAdditionalFormFieldsForPartnerUser: FormFieldItem = {
    fieldType: FormFieldType.CHECKBOX_GROUP,
    name: 'active',
    label: t(TranslationKey.Companies),
    itemsInColumn: 4,
    checkboxes: companiesData
      ?.filter(item => item.parentCompanyId === selectedParentCompany)
      .map(item => ({
        checked: userData.companyIds.includes(item.id),
        label: `${item.name} (${countries(t).find(country => country.isoCode === item.country)?.name})`,
        value: item.id,
        name: item.id,
        onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
          setUserData(prev => ({
            ...prev,
            companyIds: checked ? [...prev.companyIds, item.id] : [...prev.companyIds.filter(it => it !== item.id)],
          })),
      })),
  }

  /**
   * User form fields configuration - additional section
   */
  const userAdditionalFormFields: FormFieldItem[] = useMemo(
    () =>
      userData.role === UserRole.COMPANY_USER
        ? [
            {
              fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
              name: 'dealerId',
              label: t(TranslationKey.Dealer),
              value: userData.dealerId || undefined,
              items: filteredDealers,
              error: userFormFieldsErrors.includes('dealerId'),
              onChange: (item: UIKit.DropdownItem | null) => onChangeUserAdditionalFormField('dealerId', item),
              // Company User cannot change it's Dealer
              disabled: isCompanyUser(),
            },
          ]
        : userData.role === UserRole.PARENT_ADMINISTRATOR
          ? [
              {
                fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
                label: t(TranslationKey['Amplio partner']),
                value: selectedParentCompany,
                items: parentCompaniesOptions,
                onChange: (item: UIKit.DropdownItem | null) => setSelectedParentCompany(String(item?.value)),
              },
            ]
          : [],

    [userData.companyIds, companiesOptions, dealersData, userFormFieldsErrors, userData.dealerId],
  )

  /**
   * A custom validation function tha checks if any User form field is invalid
   * @returns If the User form data contains invalid fields
   */
  const validateData = (): boolean => {
    const notRequiredFields: string[] = ['image']

    if (!(userData.role === UserRole.COMPANY_USER)) {
      notRequiredFields.push('dealerId')
    }
    if (userData.role === UserRole.SUPER_ADMIN) {
      notRequiredFields.push('companyIds')
    }

    if (id) {
      notRequiredFields.push('password')
    }

    const invalidValues = validateFields(userData).filter(item => !notRequiredFields.includes(item))

    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })

      setUserFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  /**
   * A function that handles the Save button click
   * If form is valid updates or creates a new User
   * @returns void
   */
  const onSubmit = () => {
    if (validateData()) return

    const clearAndExit = (id: string) => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.USER_VIEW}/${id}`)
    }

    const handleSuccess = (id: string) => {
      if (imageFile) {
        postImageMutation(
          {
            id,
            file: imageFile,
            type: ImageType.USER,
          },
          {onSuccess: () => clearAndExit(id)},
        )
      } else {
        clearAndExit(id)
      }
    }

    if (id) {
      const {password, ...rest} = userData
      updateUserMutation(
        {
          id,
          body: {
            id,
            ...(rest as UserRequest),
            ...(isSuperAdmin() && password !== '' ? {password} : {}),
          },
        },
        {onSuccess: () => handleSuccess(id)},
      )
    } else {
      registerUserMutation(userData as UserRequest, {onSuccess: ({id}) => handleSuccess(id)})
    }
  }

  /**
   * A function that handles the Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  const isPartnerAdminUser = useMemo(() => userData.role === UserRole.PARENT_ADMINISTRATOR, [userData])

  const isSuperAdminUser = useMemo(() => userData.role === UserRole.SUPER_ADMIN, [userData])

  return {
    userGeneralFormFields,
    userRoleFormField,
    userAdditionalFormFields,
    imageUrl,
    userAdditionalFormFieldsForPartnerUser,
    isPartnerAdminUser,
    isSuperAdminUser,
    t,
    handleDrop,
    onDiscard,
    onSubmit,
    navigate,
  }
}
